import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { ErrorAlert } from '@repo/ui'
import { BfnStatus } from '@/models/Befehlsnachricht'
import SimpleApplyCancelDialog from '@/components/common/dialog/SimpleApplyCancelDialog'
import { postStatus } from '@/apis/Befehlsnachricht'
import { useBefehlsnachrichtUpdater } from '@/features/befehlsnachrichten/hooks/useBefehlsnachrichtUpdater'
import { Abruftoken } from '@/models/Abruftoken'

export interface BfnAcceptStandortBestaetigungDialogProps {
  abruftoken: Abruftoken
  state: boolean
  onClose: () => void
  'data-testid': string
}

export default function BfnAcceptStandortBestaetigungDialog({
  abruftoken,
  state,
  onClose,
  'data-testid': testId
}: Readonly<BfnAcceptStandortBestaetigungDialogProps>) {
  const { t } = useTranslation('bfnDetails')
  const updateBefehlsnachricht = useBefehlsnachrichtUpdater()

  const acceptMutation = useMutation({
    mutationFn: () =>
      postStatus(abruftoken, BfnStatus.STANDORTEINGABE_AKZEPTIERT),
    onSuccess: async () => {
      await updateBefehlsnachricht(abruftoken)
    }
  })

  const acceptOnApply = () => {
    acceptMutation.mutate(undefined, {
      onSuccess: () => {
        onClose()
      }
    })
  }

  return (
    <SimpleApplyCancelDialog
      title={t('dialog.accept_standort_bestaetigung.title')}
      state={state}
      onApply={acceptOnApply}
      onCancel={onClose}
      applyButtonDisabled={acceptMutation.isPending}
      applyButtonText={t('dialog.accept_standort_bestaetigung.apply')}
      cancelButtonText={t('dialog.accept_standort_bestaetigung.cancel')}
      data-testid={testId}
    >
      <p data-testid={`${testId}-text`}>
        {t('dialog.accept_standort_bestaetigung.text')}
      </p>
      {acceptMutation.isError && (
        <ErrorAlert
          error={acceptMutation.error}
          data-testid={`${testId}-error`}
        >
          {t('dialog.accept_standort_bestaetigung.error')}
        </ErrorAlert>
      )}
    </SimpleApplyCancelDialog>
  )
}
